import {
  CREATE_CONVERSATION_SUCCEEDED,
  MESSAGES_FETCH_SUCCEEDED,
  ONLY_MESSAGES_FETCH_SUCCEEDED,
  ADD_MESSAGE_FOR_NUMBER,
  CHANGE_MESSAGE_FOR_NUMBER,
  CHANGE_NEW_CONVERSATION_ID,
  CHANGE_CONVERSATION_SENDER,
  FETCH_CONTACT_DETAILS_SUCCEEDED,
  MARK_CONVERSATION_READ,
  ADD_INCOMING_MESSAGE_FOR_NUMBER,
  MARK_CONVERSATION_READ_ON_PANEL,
  SEND_MESSAGE,
  SEND_MESSAGE_SUCCEEDED,
  SEND_MESSAGE_FAILED,
  DISCARD_FAILED_MESSAGE
} from "../../constants/ActionTypes";
import { getConversationById, getConversationByNumber, isNewConversation } from "../../components/chat/ChatNew/utils";

const initialSettings = [];

const newConversationObject = (recipientNumber, senderNumber) => ({
  participants: [],
  _id: `new-${Date.now()}`,
  account: "first",
  accountNumber: senderNumber,
  clientNumber: recipientNumber,
  messages: [],
  isMessagesFetched: true,
  isRead: true,
  __v: 0
});

// Messages typed in the chat are shown straight away as temporary ones: `pending` (greyed out,
// spinner) while the request runs, `failed` (exclamation mark) if it is refused. A temporary
// message that went through is replaced by the real one when that arrives over the websocket
// (or on the next fetch of the conversation). Notes and whispers are not shown this way.
const TEMP_CHANNELS = ['sms', 'mms', 'whatsapp'];

const updateConversation = (state, clientNumber, update) =>
  state.map(conversation => (conversation.clientNumber === clientNumber ? update(conversation) : conversation));

const updateTemp = (state, clientNumber, tempId, change) =>
  updateConversation(state, clientNumber, conversation => ({
    ...conversation,
    messages: (conversation.messages || []).map(message => (message.tempId === tempId ? {...message, ...change} : message))
  }));

// The real body can carry a brand prefix and STOP text the typed one does not.
const isRealOf = (real, temp) =>
  !temp.failed && typeof real.body === 'string' && real.body.includes(temp.body);

const messages = (state = initialSettings, action) => {
  const conversationList = [...state];
  switch (action.type) {
    case MESSAGES_FETCH_SUCCEEDED:
      return action.forceReload ? action.messages : [
        ...state.filter(existingConversation => action.messages.find(conversation => existingConversation.conversationId === conversation.conversationId)),
        ...action.messages.filter(conversation => !state.find(existingConversation => existingConversation.conversationId === conversation.conversationId))
      ];
    case ONLY_MESSAGES_FETCH_SUCCEEDED: {
        const newConversations = state.map(
            (conversation) => {
                if (conversation.conversationId !== action.conversationId) return conversation;
                // Keep what is still on its way or failed; the rest is in the fetched list.
                const temps = (conversation.messages || []).filter(message => message.tempId && (message.pending || message.failed));
                return { ...conversation, messages: [...(action.messages || []), ...temps], isMessagesFetched: true };
            }
        );
        return [...newConversations];
    }
    case SEND_MESSAGE: {
      if (!TEMP_CHANNELS.includes(action.channel)) return state;
      const temp = {
        tempId: action.tempId,
        messageId: action.tempId,
        pending: true,
        body: action.message,
        from: action.senderNumber,
        to: action.recipientNumber,
        clientNumber: action.recipientNumber,
        direction: 'out',
        channel: action.channel,
        platform: action.channel,
        time: action.sentAt,
        retry: {
          message: action.message,
          recipientNumber: action.recipientNumber,
          senderNumber: action.senderNumber,
          channel: action.channel,
          mmsFile: action.mmsFile
        }
      };
      return updateConversation(state, action.recipientNumber, conversation =>
        conversation.messages ? {...conversation, messages: [...conversation.messages, temp]} : conversation);
    }
    case SEND_MESSAGE_SUCCEEDED:
      return action.tempId ? updateTemp(state, action.clientNumber, action.tempId, {pending: false}) : state;
    case SEND_MESSAGE_FAILED:
      return action.tempId ? updateTemp(state, action.clientNumber, action.tempId, {pending: false, failed: true, error: action.error}) : state;
    case DISCARD_FAILED_MESSAGE:
      return updateConversation(state, action.clientNumber, conversation => ({
        ...conversation,
        messages: (conversation.messages || []).filter(message => message.tempId !== action.tempId)
      }));
    case CREATE_CONVERSATION_SUCCEEDED:
      return [
        ...state,
        newConversationObject(action.recipientNumber, action.senderNumber)
      ];
    case ADD_MESSAGE_FOR_NUMBER:
      const conversationToExtend = getConversationByNumber(conversationList, action.message.clientNumber);
      if (conversationToExtend) {
        conversationToExtend.lastMessage = action.message;
        if (conversationToExtend.messages) {
          // The real message replaces the temporary one shown while it was being sent.
          const tempIndex = conversationToExtend.messages.findIndex(message => message.tempId && isRealOf(action.message, message));
          conversationToExtend.messages = tempIndex === -1
            ? [...conversationToExtend.messages, action.message]
            : [...conversationToExtend.messages.slice(0, tempIndex), ...conversationToExtend.messages.slice(tempIndex + 1), action.message];
        }
      }
      return [...conversationList];
    case ADD_INCOMING_MESSAGE_FOR_NUMBER:
      const conversationToExtension = getConversationByNumber(conversationList, action.message.clientNumber);
      if (conversationToExtension) {
        conversationToExtension.lastMessage = action.message;
        conversationToExtension.isRead = false;
        if (conversationToExtension.messages) {
          conversationToExtension.messages.push(action.message);
        }
      }
      return [...conversationList];
    case CHANGE_NEW_CONVERSATION_ID:
      const conversationToChange = getConversationByNumber(conversationList, action.clientNumber);
      if (isNewConversation(conversationToChange._id)) {
          conversationToChange._id = action.id;
      }
      return [...conversationList];
    case CHANGE_MESSAGE_FOR_NUMBER:
      const conversationToChangeMessage = getConversationByNumber(conversationList, action.message.clientNumber);
      if (conversationToChangeMessage && (!conversationToChangeMessage.status || !['UNDELIVERED', 'DELIVERED', 'READ'].includes(conversationToChangeMessage.status))) {
        conversationToChangeMessage.lastMessage = conversationToChangeMessage.lastMessage.messageId === action.message.messageId ? action.message : conversationToChangeMessage.lastMessage;
        conversationToChangeMessage.messages = conversationToChangeMessage.messages.map(message => message.messageId === action.message.messageId ? {...action.message, last: true} : message);
      }
      return [...conversationList];
    case CHANGE_CONVERSATION_SENDER:
      const conversationToChangeSender = getConversationByNumber(conversationList, action.clientNumber);
      conversationToChangeSender.accountNumber = action.senderNumber;
      return [...conversationList];
    case FETCH_CONTACT_DETAILS_SUCCEEDED:
      const conversationToSetContact = getConversationByNumber(conversationList, action.contact.phone);
        conversationToSetContact.contactDetails = action.contact;
      return [...conversationList];
    case MARK_CONVERSATION_READ:
    case MARK_CONVERSATION_READ_ON_PANEL:
      const conversationToMarkRead = getConversationById(conversationList, action.conversationId);

      if (!conversationToMarkRead.isRead) {
        conversationToMarkRead.isRead = true;
      }

      return [...conversationList];
    default:
      return state;
  }
};

export default messages;
